import * as angular from 'angular';

import { WideError } from '../../common/WideError';
import { State } from '../../models';
import { StateDto } from '../../models/DTO';
import { ErrorLevel } from '../../models/enum';

import { IRequestService, IGroupService } from '../../services';
import { IFilePathsHelper } from '../helpers';

export interface IStateService {
    getAll(): angular.IPromise<StateDto[]>;
    getAll(force: boolean): angular.IPromise<StateDto[]>;
    getByGuid(guid: string): angular.IPromise<StateDto>;
    getIcon(icon: string): string;
    getUsedByGroup(groupGuid: string): angular.IPromise<StateDto[]>;
    updateOne(state: StateDto): angular.IPromise<StateDto>;
    deleteOne(stateGuid: string): angular.IPromise<boolean>;
    addToGroup(groupGuid: string, state: StateDto, identifierGuid: string): angular.IPromise<StateDto>;
    deleteFromGroup(stateGuid: string, groupGuid: string, identifierGuid: string): angular.IPromise<boolean>;
    isErrors(state: State): WideError[];
}

export class StateService implements IStateService {
    static serviceId = 'stateService';

    private _controller: string;
    private _usedByGroup: string;

    static $inject = [
        'requestService',
        'filePathsHelper',
        'groupService'
    ];

    constructor(
        private _requestService: IRequestService,
        private _filePathsHelper: IFilePathsHelper,
        private _groupService: IGroupService
    ) {
        this._controller = 'state/';
        this._usedByGroup = `${this._controller}used/`;
    }

    getAll(force?: boolean): angular.IPromise<StateDto[]> {
        if (force) {
            this._requestService.cleanCacheAtV1(this._controller);
        }
        return this._requestService
            .getAtV1<StateDto[]>(this._controller)
            .then(states => {
                states.forEach(s => { s.style = this.computeStyle(s); });
                return states;
            });
    }

    getByGuid(guid: string): angular.IPromise<StateDto> {
        const url = `${this._controller}${guid}/`;
        return this._requestService
            .getAtV1<StateDto>(url)
            .then(state => {
                state.style = this.computeStyle(state);
                return state;
            });
    }

    getIcon(icon: string): string {
        const contentUrl = this._filePathsHelper.getContentUrl();
        return (icon && contentUrl)
            ? contentUrl + icon.replace(new RegExp('\\\\', 'g'), '/').replace('~/Content', '')
            : '';
    }

    getUsedByGroup(groupGuid: string): angular.IPromise<StateDto[]> {
        const url = `${this._usedByGroup}${groupGuid}/`;
        return this._requestService
            .getAtV1<StateDto[]>(url)
            .then(states => {
                states.forEach(s => { s.style = this.computeStyle(s); });
                return states;
            });
    }

    updateOne(state: StateDto): angular.IPromise<StateDto> {
        const url = `${this._controller}${state.state.guid}/`;
        return this._requestService
            .postAtV1<StateDto>(url, state)
            .then(state => {
                state.style = this.computeStyle(state);
                this._requestService.cleanCacheAtV1(this._controller);
                this._requestService.cleanCacheAtV1(url);
                this._groupService.cleanGroupCache();
                return state;
            });
    }

    addToGroup(groupGuid: string, state: StateDto, identifierGuid: string): angular.IPromise<StateDto> {
        const url = `${this._controller}group/${groupGuid}/`;
        return this._requestService
            .postAtV1<StateDto>(url, state)
            .then(data => {
                this._requestService.cleanCacheAtV1(url);
                this._requestService.cleanCacheAtV1(`${this._usedByGroup}${groupGuid}/`);
                this._requestService.cleanCacheAtV1(`group${groupGuid}/`);
                this._requestService.cleanCacheAtV1(`identifier/rules/${identifierGuid}/`);
                this._groupService.cleanGroupCache();
                return data;
            });
    }

    deleteFromGroup(stateGuid: string, groupGuid: string, identifierGuid: string): angular.IPromise<boolean> {
        const url = `${this._controller}group/${groupGuid}/`;
        const dto = { state: { guid: stateGuid } };
        return this._requestService
            .deleteAtV1(url, dto)
            .then(data => {
                this._requestService.cleanCacheAtV1(url);
                this._requestService.cleanCacheAtV1(`${this._usedByGroup}${groupGuid}/`);
                this._requestService.cleanCacheAtV1('group/' + groupGuid + '/');
                this._requestService.cleanCacheAtV1('identifier/rules/' + identifierGuid + '/');
                this._groupService.cleanGroupCache();
                return data;
            });
    }

    deleteOne(stateGuid: string): angular.IPromise<boolean> {
        const url = `${this._controller}${stateGuid}/`;
        return this._requestService
            .deleteAtV1(url)
            .then(data => {
                this._requestService.cleanCacheAtV1(this._controller);
                this._requestService.cleanCache(url);
                this._groupService.cleanGroupCache();
                return data;
            });
    }

    private computeStyle(state: any): string {
        return `{'background-color': '${state.state.markerColorString}'}`;
    }

    isErrors(state: State): WideError[] {
        const result: WideError[] = [];
        if (!state.guid) {
            result.push(this.makeError('Cannot find state GUID'));
        }
        if (!state.name) {
            result.push(this.makeError('Cannot find state Name'));
        }
        if (!state.markerColorString) {
            result.push(this.makeError('State has no color associated'));
        }
        return result;
    }

    private makeError(msg: string): WideError {
        return new WideError(ErrorLevel.Error, msg);
    }
}